// ============================================================================
// quote-jobs/imported-quote.ts - is this quote one the operator imported?
// (docs/job/GOAL.md C1, decision D6 of 2026-09-25).
// ----------------------------------------------------------------------------
// POST /api/quote-jobs/import stamps quote_data.meta.importSource
// ({ name, kind, sha256, byteLength, importedAt }) in
// backend/src/services/quote-import-request.js buildImportedProjectRow. A Split
// or Duplicate deletes that marker (quote-split.js splitQuoteEnvelope,
// project-duplication.ts resetQuoteNumber), and a regenerated quote starts
// from a fresh meta, so only the imported quote and its revisions carry it.
//
// The marker is the only test. sourceQuoteRef is not: an imported RJ Bird
// quote keeps its own number and has no sourceQuoteRef. workflow_mode is not
// either.
//
// Pure. No imports, so the readiness gate and the pipeline audit can both use
// it without pulling anything else in.
// ============================================================================

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** True when quote_data carries the import marker (camelCase or snake_case). */
export function isImportedQuote(quoteData: unknown): boolean {
    if (!isRecord(quoteData)) return false;
    const meta = quoteData.meta;
    if (!isRecord(meta)) return false;
    return isRecord(meta.importSource) || isRecord(meta.import_source);
}

/**
 * D6: an imported quote with no Internal Price Working is not asked to verify
 * one. Its prices came from the document, not from a QuoteMaker costing, so
 * "the working is missing" can never clear without rebuilding the quote with
 * AI. Once the operator does build a costing sheet, every pricing check applies
 * again (the sheet can go stale like any other).
 */
export function importedQuoteWithoutInternalWorking(quoteData: unknown, costingSheet: unknown): boolean {
    return isImportedQuote(quoteData) && !costingSheet;
}

export default { isImportedQuote, importedQuoteWithoutInternalWorking };
